import React from "react";
import { useTranslation } from "react-i18next";
import Modal from "react-bootstrap/Modal";
import { getShandeExplanation } from "../helpers/StaticData/aboutUsData";
import { SplitBRString, replaceStringWithShandeAge, replaceStringWithShandeChildAmount, replaceStringWithShandeTotalHelpedChildAmount } from "../helpers/dataHelper";
import SEOComponent from "../components/SEOComponent";
import Reveal from "../components/animations/Reveal";
import { useGsap } from "../animations/hooks";

function AboutUs() {
    const {t} = useTranslation();
    const [selectedLicense, setSelectedLicense] = React.useState(null);
    const photoUrl = process.env.REACT_APP_SHANDE_PHOTO_URL;
    const aboutDescription = replaceStringWithShandeTotalHelpedChildAmount(replaceStringWithShandeChildAmount(replaceStringWithShandeAge(t("aboutUsDescription"))));
    const shandeExplanation = getShandeExplanation();
    const stats = [
        { value: "2011", label: t("heroTagline") },
        { value: replaceStringWithShandeAge("[shandeAge]"), label: t("statYearsLabel") },
        { value: replaceStringWithShandeTotalHelpedChildAmount("[shandeTotalHelpedChildrenAmount]"), label: t("statHelpedLabel") },
    ];
    // Hero entrance: copy rises in sequence while the photo settles into place.
    const heroRef = useGsap((gsap, el) => {
        gsap.timeline({ defaults: { ease: "power3.out" } })
            .from(el.querySelector(".landing-page-kicker"), { y: 18, autoAlpha: 0, duration: 0.5 })
            .from(el.querySelector(".landing-page-title"), { y: 36, autoAlpha: 0, duration: 0.8 }, "-=0.25")
            .from(el.querySelectorAll(".landing-page-summary li"), { y: 20, autoAlpha: 0, duration: 0.6, stagger: 0.08 }, "-=0.45")
            .from(el.querySelectorAll(".landing-page-pill"), { y: 20, autoAlpha: 0, duration: 0.5, stagger: 0.1 }, "-=0.3")
            .from(el.querySelector(".landing-page-hero-frame"), { x: 40, scale: 0.95, autoAlpha: 0, duration: 1 }, 0.3)
            .from(el.querySelector(".landing-page-hero-note"), { y: 20, autoAlpha: 0, duration: 0.6 }, "-=0.4");
    });
    const licenses = [
        { img: `${photoUrl}/about_us/license/jkm_license.jpg`, alt: "JKM license" },
        { img: `${photoUrl}/about_us/license/jppm_license.jpg`, alt: "JPPM license" },
        { img: `${photoUrl}/about_us/license/sijil_pendaftaran.jpg`, alt: "Registration certificate" },
        { img: `${photoUrl}/about_us/license/eq_sammi.jpg`, alt: "EQ certificate" },
    ];

    return (
        <>
        {selectedLicense ?
            <Modal
            show={true}
            onHide={() => setSelectedLicense(null)}
            centered
            size="lg"
            aria-labelledby="about-license-preview"
            >
            <Modal.Header closeButton>
                <Modal.Title id="about-license-preview">{selectedLicense?.alt}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <img className="landing-page-license-preview" src={selectedLicense?.img} alt={selectedLicense?.alt} />
            </Modal.Body>
            </Modal>
            : ""
        }
        <SEOComponent 
            title="About Us"
            description="Learn about Shan De Children's Welfare Home, established in 2011 in Johor Bahru. Our mission is to help disadvantaged children from all races regardless of religious beliefs. Discover our history, mission, and vision."
            keywords="about us, history, mission, vision, charity background, non-profit story"
            url="/aboutUs"
        />
        <section ref={heroRef} className="landing-page-hero">
            <div className="container">
                <div className="landing-page-hero-grid">
                    <div className="landing-page-hero-copy">
                        <div className="landing-page-kicker">{t("aboutEyebrow")}</div>
                        <h1 className="landing-page-title">{t("aboutUs")}</h1>
                        <div className="landing-page-summary">{SplitBRString(aboutDescription)}</div>
                        <div className="landing-page-meta">
                            {stats.map((item) => (
                                <div className="landing-page-pill" key={item.label}>
                                    <div className="landing-page-pill-value">{item.value}</div>
                                    <div className="landing-page-pill-label">{item.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="landing-page-hero-media">
                        <div className="landing-page-hero-frame">
                            <img className="landing-page-hero-image" src="assets/img/about_us/house-cartoon.jpg" alt={t("aboutUs")} />
                        </div>
                        <div className="landing-page-hero-note">
                            <strong>{t("aboutFooter")}</strong>
                            <span>{t("aboutImageCaption")}</span>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section className="landing-section landing-section-alt">
            <div className="container">
                <Reveal className="landing-page-section-title">
                    <div className="landing-eyebrow">{t("aboutShande")}</div>
                    <h2 className="landing-heading">{t("plansTitle")}</h2>
                    <p className="landing-section-desc">{t("heroDescription")}</p>
                </Reveal>
                <Reveal className="landing-page-card-grid" stagger={0.15}>
                    <div className="landing-page-card" data-reveal>
                        <h3>{t("ourTarget")}</h3>
                        <p>{t("ourTargetDescription")}</p>
                    </div>
                    <div className="landing-page-card" data-reveal>
                        <h3>{t("ourVision")}</h3>
                        <p>{t("ourVisionDescription")}</p>
                    </div>
                </Reveal>
            </div>
        </section>

        <section className="landing-section landing-section-soft">
            <div className="container">
                <Reveal className="landing-page-section-title center">
                    <div className="landing-eyebrow">{t("trustRegisteredTitle")}</div>
                    <h2 className="landing-heading">{t("ourLicense")}</h2>
                    <p className="landing-section-desc">{t("trustAuditDesc")}</p>
                </Reveal>
                <Reveal className="landing-page-license-grid" stagger={0.12}>
                    {licenses.map((item) => (
                        <button className="landing-page-license-card" data-reveal key={item.alt} type="button" onClick={() => setSelectedLicense(item)}>
                            <img src={item.img} alt={item.alt} />
                        </button>
                    ))}
                </Reveal>
            </div>
        </section>

        <section className="landing-section landing-section-alt">
            <div className="container">
                <Reveal className="landing-page-section-title center">
                    <div className="landing-eyebrow">{t("aboutUs")}</div>
                    <h2 className="landing-heading">S H A N D E</h2>
                    <p className="landing-section-desc">{t("aboutP2")}</p>
                </Reveal>
                <div className="landing-page-shande-grid">
                    {shandeExplanation && shandeExplanation.map((item, index) => (
                        <Reveal as="article" className={`landing-page-shande-card${index % 2 === 1 ? " reverse" : ""}`} stagger={0.08} key={item?.title}>
                            <div className="landing-page-shande-media" data-reveal>
                                <img src={item?.img} alt={item?.title} />
                            </div>
                            <div>
                                <h3 className="landing-page-shande-title" data-reveal>{item?.title}</h3>
                                <div className="landing-page-shande-list">
                                    {item?.text?.map((text, textIndex) => (
                                        <div className="landing-page-shande-line" data-reveal key={text}>
                                            <span className="landing-page-shande-number">{textIndex + 1}</span>
                                            <span>{text}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
        </>
    )
}
export default AboutUs;